import React, { useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useRouteMatch } from 'react-router-dom'
import {
  fetchVehicleSnapshots,
  createVehicleSnapshot,
  deleteVehicleSnapshot,
  fetchVehicleAsBuilt,
} from 'actions/vehicleSnapshots'
import {
  getVehicleSnapshots,
  getVehicleSnapshotsLoading,
  getVehicleSnapshotsSaving,
  getVehicleSnapshotsError,
  isAtProfileLimit,
  getDeletingSnapshotIds,
  getAllVehiclesSnapshotData,
  getVehicleAsBuilt,
  MAX_SNAPSHOTS_PER_VEHICLE,
  MAX_SNAPSHOTS_PER_PROFILE,
} from 'selectors/vehicleSnapshots'
import { Icon, Tooltip } from 'components/patterns'
import { Icon__Snapshot } from 'components/patterns/Icon/components'
import VehicleSnapshotCard from 'components/VehicleSnapshotCard'
import VehicleSnapshotModal from 'components/VehicleSnapshotModal'
import { setDrawerWidth, setDrawerVisibleWidth } from 'actions/drawer'
import { DEFAULT_DRAWER_WIDTH } from 'utils/drawerConstants'
import {
  FlyoutOuter,
  DrawerWrapper,
  DrawerHeader,
  DrawerTitle,
  DrawerContent,
  SkeletonCard,
  NoSnapshotsCard,
  NoSnapshotsCardText,
  SectionHeader,
  SectionWrapper,
} from './VehicleSnapshotsFlyout.style'

const VehicleSnapshotsFlyout = () => {
  const dispatch = useDispatch()
  const match = useRouteMatch('/vehicles/:serialNumber')
  const serialNumber = match ? match.params.serialNumber : null

  const snapshots = useSelector((state) =>
    serialNumber ? getVehicleSnapshots(state, { serialNumber }) : []
  )
  const loading = useSelector((state) =>
    serialNumber ? getVehicleSnapshotsLoading(state, { serialNumber }) : false
  )
  const saving = useSelector((state) =>
    serialNumber ? getVehicleSnapshotsSaving(state, { serialNumber }) : false
  )
  const error = useSelector((state) =>
    serialNumber ? getVehicleSnapshotsError(state, { serialNumber }) : null
  )
  const atProfileLimit = useSelector(isAtProfileLimit)
  const deletingIds = useSelector((state) =>
    serialNumber ? getDeletingSnapshotIds(state, { serialNumber }) : []
  )
  const allVehiclesData = useSelector(getAllVehiclesSnapshotData)
  const asBuiltData = useSelector((state) =>
    serialNumber ? getVehicleAsBuilt(state, { serialNumber }) : null
  )

  const vehicleInfoMap = useSelector((state) => {
    const result = {}

    // Seed from favorites and recently-viewed — both contain isLeadSerial from
    // the API and are available even when the user hasn't navigated to the
    // vehicle in this session (truck.vehicles is only populated by getTruckDetails).
    const favorites = state.getIn(['user', 'favoriteVehicles'])
    if (favorites) {
      favorites.forEach((v) => {
        const sn = v.get('serialNumber')
        if (sn) {
          result[sn] = {
            modelDescription: v.get('vehicleModel') || v.get('model') || null,
            isLead: !!v.get('isLeadSerial'),
          }
        }
      })
    }
    const recentlyViewed = state.getIn(['user', 'recentlyViewedVehicles'])
    if (recentlyViewed) {
      recentlyViewed.forEach((v) => {
        const sn = v.get('serialNumber')
        if (sn) {
          result[sn] = {
            modelDescription: v.get('vehicleModel') || v.get('model') || null,
            isLead: !!v.get('isLeadSerial'),
          }
        }
      })
    }

    // Authoritative overrides from truck.vehicles (populated when user navigates
    // to a specific vehicle page and getTruckDetails fires).
    const vehicles = state.getIn(['truck', 'vehicles'])
    if (vehicles) {
      vehicles.forEach((v, sn) => {
        result[sn] = {
          modelDescription: v.get('vehicleModel') || null,
          isLead: !!v.get('isLeadSerial'),
        }
      })
    }

    return result
  })

  // Combine truck vehicles (current session), recently viewed, and favorites so
  // the flyout can fetch snapshot data even on a fresh session.
  const allKnownSerialNumbers = useSelector((state) => {
    const seen = new Set()
    const excluded = new Set()
    const truckVehicles = state.getIn(['truck', 'vehicles'])
    if (truckVehicles)
      truckVehicles.forEach((v, sn) => {
        if (v.get('isPreProduction') === true) {
          excluded.add(sn)
        } else if (v.get('isPreProduction') === false) {
          seen.add(sn)
        }
      })
    const recentlyViewed = state.getIn(['user', 'recentlyViewedVehicles'])
    if (recentlyViewed)
      recentlyViewed.forEach((v) => {
        const sn = v.get('serialNumber')
        if (sn && !excluded.has(sn)) seen.add(sn)
      })
    const favorites = state.getIn(['user', 'favoriteVehicles'])
    if (favorites)
      favorites.forEach((v) => {
        const sn = v.get('serialNumber')
        if (sn && !excluded.has(sn)) seen.add(sn)
      })
    return Array.from(seen)
  })

  const [fetchInitiated, setFetchInitiated] = useState(false)

  // Issue 2: Reset drawer width to default when snapshots panel opens, so it
  // is never affected by the S&G "Table View" expanded width (1025px).
  useEffect(() => {
    dispatch(setDrawerWidth(DEFAULT_DRAWER_WIDTH))
    dispatch(setDrawerVisibleWidth(DEFAULT_DRAWER_WIDTH))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []) // intentionally runs once on mount

  useEffect(() => {
    allKnownSerialNumbers.forEach((sn) => dispatch(fetchVehicleSnapshots(sn)))
    setFetchInitiated(true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []) // intentionally runs once on mount

  useEffect(() => {
    if (serialNumber) {
      dispatch(fetchVehicleAsBuilt(serialNumber))
    }
  }, [dispatch, serialNumber])

  const otherVehicles = allVehiclesData
    .filter((v) => v.serialNumber !== serialNumber && v.snapshots.length > 0)
    .sort((a, b) =>
      a.serialNumber.localeCompare(b.serialNumber, undefined, {
        numeric: true,
        sensitivity: 'base',
      })
    )

  const handleCapture = () => {
    if (serialNumber && !saving && !atProfileLimit) {
      dispatch(createVehicleSnapshot(serialNumber))
    }
  }

  const handleDeleteCurrent = (snapshotId) => {
    if (serialNumber) {
      dispatch(deleteVehicleSnapshot(serialNumber, snapshotId))
    }
  }

  const handleDeleteOther = (otherSerialNumber, snapshotId) => {
    dispatch(deleteVehicleSnapshot(otherSerialNumber, snapshotId))
  }

  // viewingModal = { snapshots, serialNumber, initialSnapshotId } | null
  const [viewingModal, setViewingModal] = useState(null)

  if (serialNumber === null) {
    const isInitialLoad =
      !fetchInitiated || allVehiclesData.some((v) => v.loading)
    const vehiclesWithSnapshots = allVehiclesData
      .filter((v) => v.snapshots.length > 0)
      .sort((a, b) =>
        a.serialNumber.localeCompare(b.serialNumber, undefined, {
          numeric: true,
          sensitivity: 'base',
        })
      )

    return (
      <FlyoutOuter>
        {viewingModal && (
          <VehicleSnapshotModal
            snapshots={viewingModal.snapshots}
            serialNumber={viewingModal.serialNumber}
            initialSnapshotId={viewingModal.initialSnapshotId}
            onClose={() => setViewingModal(null)}
          />
        )}
        <DrawerWrapper>
          <DrawerHeader>
            <div style={{ display: 'flex', alignItems: 'center', gap: '7px' }}>
              {/* eslint-disable-next-line react/jsx-pascal-case */}
              <Icon__Snapshot
                width={24}
                height={17}
                fill="rgba(79, 87, 100, 0.87)"
              />
              <div
                style={{ display: 'flex', alignItems: 'center', gap: '4px' }}
              >
                <DrawerTitle>Vehicle Snapshots</DrawerTitle>
                <Tooltip
                  title={`Snapshots save a temporary record of your vehicle's configuration. You can save ${MAX_SNAPSHOTS_PER_VEHICLE} snapshots per vehicle and up to ${MAX_SNAPSHOTS_PER_PROFILE} on your account. Snapshots expire after 120 days.`}
                >
                  <Icon
                    icon="question_mark_tooltip"
                    fill="outlined"
                    size={14}
                  />
                </Tooltip>
              </div>
            </div>
          </DrawerHeader>
          <DrawerContent>
            {isInitialLoad ? (
              <>
                <SkeletonCard />
                <SkeletonCard />
              </>
            ) : vehiclesWithSnapshots.length === 0 ? (
              <NoSnapshotsCard>
                <NoSnapshotsCardText>
                  You have not captured any vehicle snapshots.
                </NoSnapshotsCardText>
              </NoSnapshotsCard>
            ) : (
              vehiclesWithSnapshots.map((v) => (
                <VehicleSnapshotCard
                  key={v.serialNumber}
                  serialNumber={v.serialNumber}
                  snapshots={v.snapshots}
                  modelDescription={
                    vehicleInfoMap[v.serialNumber]?.modelDescription
                  }
                  isLead={vehicleInfoMap[v.serialNumber]?.isLead}
                  deletingIds={v.deletingIds}
                  error={v.error}
                  onView={(snapshot) =>
                    setViewingModal({
                      snapshots: v.snapshots,
                      serialNumber: v.serialNumber,
                      initialSnapshotId: snapshot.id,
                    })
                  }
                  onDelete={(snapshotId) =>
                    handleDeleteOther(v.serialNumber, snapshotId)
                  }
                />
              ))
            )}
          </DrawerContent>
        </DrawerWrapper>
      </FlyoutOuter>
    )
  }

  return (
    <FlyoutOuter>
      {viewingModal && (
        <VehicleSnapshotModal
          snapshots={viewingModal.snapshots}
          serialNumber={viewingModal.serialNumber}
          initialSnapshotId={viewingModal.initialSnapshotId}
          initialSection={viewingModal.initialSection}
          asBuiltData={
            viewingModal.serialNumber === serialNumber ? asBuiltData : null
          }
          onClose={() => setViewingModal(null)}
        />
      )}
      <DrawerWrapper>
        <DrawerHeader>
          <div style={{ display: 'flex', alignItems: 'center', gap: '7px' }}>
            {/* eslint-disable-next-line react/jsx-pascal-case */}
            <Icon__Snapshot
              width={24}
              height={17}
              fill="rgba(79, 87, 100, 0.87)"
            />
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <DrawerTitle>Vehicle Snapshots</DrawerTitle>
              <Tooltip
                title={`Snapshots save a temporary record of your vehicle's configuration. You can save ${MAX_SNAPSHOTS_PER_VEHICLE} snapshots per vehicle and up to ${MAX_SNAPSHOTS_PER_PROFILE} on your account. Snapshots expire after 120 days.`}
              >
                <Icon icon="question_mark_tooltip" fill="outlined" size={14} />
              </Tooltip>
            </div>
          </div>
        </DrawerHeader>
        <DrawerContent>
          <SectionWrapper>
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <SectionHeader>Current Vehicle</SectionHeader>
              {(snapshots.length >= MAX_SNAPSHOTS_PER_VEHICLE ||
                atProfileLimit) && (
                <Tooltip
                  title={`You've reached your snapshot limit (${MAX_SNAPSHOTS_PER_VEHICLE} per vehicle, ${MAX_SNAPSHOTS_PER_PROFILE} per account). Delete an existing snapshot to create a new one.`}
                >
                  <Icon
                    icon="question_mark_tooltip"
                    fill="outlined"
                    size={14}
                  />
                </Tooltip>
              )}
            </div>
            {loading && snapshots.length === 0 ? (
              <>
                <SkeletonCard />
                <SkeletonCard />
                <SkeletonCard />
              </>
            ) : (
              <VehicleSnapshotCard
                serialNumber={serialNumber || ''}
                snapshots={snapshots}
                modelDescription={
                  vehicleInfoMap[serialNumber]?.modelDescription
                }
                isLead={vehicleInfoMap[serialNumber]?.isLead}
                isCurrentVehicle
                saving={saving}
                atProfileLimit={atProfileLimit}
                deletingIds={deletingIds}
                error={error}
                onCapture={handleCapture}
                onDelete={handleDeleteCurrent}
                onView={(snapshot) =>
                  setViewingModal({
                    snapshots,
                    serialNumber,
                    initialSnapshotId: snapshot.id,
                  })
                }
                asBuiltData={asBuiltData}
                onViewAsBuilt={() =>
                  setViewingModal({
                    snapshots,
                    serialNumber,
                    initialSnapshotId: null,
                    initialSection: 'As-Built',
                  })
                }
              />
            )}
          </SectionWrapper>
          <SectionWrapper>
            <SectionHeader>Other Vehicle Snapshots</SectionHeader>
            {otherVehicles.length === 0 ? (
              <NoSnapshotsCard>
                <NoSnapshotsCardText>
                  No snapshots have been captured for other vehicles.
                </NoSnapshotsCardText>
              </NoSnapshotsCard>
            ) : (
              otherVehicles.map((v) => (
                <VehicleSnapshotCard
                  key={v.serialNumber}
                  serialNumber={v.serialNumber}
                  snapshots={v.snapshots}
                  modelDescription={
                    vehicleInfoMap[v.serialNumber]?.modelDescription
                  }
                  isLead={vehicleInfoMap[v.serialNumber]?.isLead}
                  deletingIds={v.deletingIds}
                  error={v.error}
                  onView={(snapshot) =>
                    setViewingModal({
                      snapshots: v.snapshots,
                      serialNumber: v.serialNumber,
                      initialSnapshotId: snapshot.id,
                    })
                  }
                  onDelete={(snapshotId) =>
                    handleDeleteOther(v.serialNumber, snapshotId)
                  }
                />
              ))
            )}
          </SectionWrapper>
        </DrawerContent>
      </DrawerWrapper>
    </FlyoutOuter>
  )
}

export default VehicleSnapshotsFlyout
